import { useTheme } from "@stores/ThemeContext";
import { useState, useEffect, useContext } from "react";
import ShirtIcon from "./ShirtIcon";
import { IPlayer } from "@components/StatsConsole/Widgets/LineupWidget/parts/Players";
import { StateContext } from "../../../../../state/stateContext";
import { useColorContext } from "@stores/ColorContext";
import { useVersion } from "@utils/hooks/useVersion";
import { T } from "@utils/enums";

interface ShirtProps {
    team: 'home' | 'away';
    player: IPlayer;
    isHorizontalList?: boolean;
    isInjuries?: boolean;
    isSmallShirt?: boolean;
    isLineup?: boolean;
}

const ShirtV2 = ({ team, player, isHorizontalList = false, isInjuries = false, isSmallShirt = false, isLineup = false }: ShirtProps) => {
    const { theme } = useTheme();
    const { colors } = useColorContext();
    const { isV2 } = useVersion();
    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { lineupTabActiveFilter } = stContext || {};

    const shirtColor = team === 'home' ? colors?.homeColor : colors?.awayColor;
    // Stroke (outline) follows theme for consistency with Stats/Players tab borders (TC_TEAM-COLOURS-002)
    const strokeColor = theme === T.LIGHT ? '#000000' : '#FFFFFF';

    return (
        <div className="h-full">
            <div className={`relative ${isSmallShirt ? 'w-[58px]' : 'w-[60px]'} h-full flex justify-center items-center`}>
                <div className={`absolute top-0 left-0 bg-transparent min-w-[16px] h-full z-30 ${isHorizontalList ? 'mt-[3px]' : 'mt-[2px]'}`}>
                    {player?.substitutionTime && !isInjuries ? <ShirtIcon type='substitutionTime' substitutionTime={player?.substitutionTime} mb="mb-[0px]" isLineup={isLineup}/> : <ShirtIcon type='empty' mb="mb-[0px]" isLineup={isLineup}/>} {/* TODO: add isPlayerInPlay */}
                    {player?.substitutionTime && !isInjuries ? <ShirtIcon type='substitution' isHorizontalList={isHorizontalList} isLineup={isLineup}/> : <ShirtIcon type='empty' isLineup={isLineup}/>}

                    {player?.goal && player?.own_goal ? <ShirtIcon type='goalAndOwnGoal' goals={player?.goal} own_goal={player?.own_goal} mb="mb-[0px]" isHorizontalList={isHorizontalList} isLineup={isLineup} /> : null}
                    
                    {player?.goal && !isInjuries && !player?.own_goal ? <ShirtIcon type='goal' goals={player?.goal} mb="mb-[0px]" isHorizontalList={isHorizontalList} isLineup={isLineup} /> : null}
                    {player?.own_goal && !isInjuries && !player?.goal ? <ShirtIcon type='ownGoal' own_goal={player?.own_goal} mb="mb-[0px]" isHorizontalList={isHorizontalList} isLineup={isLineup} /> : null}
                </div>
                <div className={`w-[32px] h-[32px] flex justify-center items-center mt-[14px]`}>
                    <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M28.9431 3.24598L22.2532 0.296561C21.5243 -0.0255023 20.6994 -0.0763544 19.9253 0.110103C18.8743 0.364364 17.5352 0.624274 16.603 0.624274C15.6707 0.624274 14.3203 0.364364 13.2693 0.110103C12.5009 -0.0763544 11.6873 -0.0311525 10.964 0.28526L4.45496 3.10473L3.90689 3.37029C1.65244 4.52293 0.217285 6.84518 0.217285 9.39908V12.1055C0.217285 13.7498 1.39818 15.1567 3.01415 15.4448L6.43254 16.0494V28.5873C6.43254 29.9829 7.30267 31.209 8.68133 31.4406C10.0939 31.6779 12.1223 31.9039 14.7497 31.9774L17.394 32C20.5468 31.9604 22.9199 31.7175 24.5189 31.4463C25.8976 31.2146 26.7677 29.9885 26.7677 28.5929V16.0551L30.1861 15.4505C31.8077 15.1623 32.983 13.7554 32.983 12.1112V9.46123C32.983 6.77737 31.3953 4.34213 28.9374 3.25728L28.9431 3.24598Z"
                            fill={shirtColor}
                            {...(isV2 && { stroke: strokeColor, strokeWidth: "0.5" })} />
                    </svg>
                </div>
                <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-[5px]
                            font-robotoRegular font-normal text-[12px] leading-[16px]`}
                            style={{ color: team === 'home' ? colors?.textAdjustedToBGHome : colors?.textAdjustedToBGAway }}
                        >
                    {player?.number}
                </div>
                <div className={`absolute top-0 right-0 bg-transparent w-[16px] h-full z-30 ${isHorizontalList ? 'mt-[1px]' : ''}`}>
                    {player?.injury && ((lineupTabActiveFilter === 'NULL') || (!player.xGoals && !player.shots && !player.ballTouches)) ? (
                        <ShirtIcon type='injury' isHorizontalList={isHorizontalList} isLineup={isLineup} />
                    ) : (
                        player.xGoals && lineupTabActiveFilter === 'XGOALS' ? <ShirtIcon type='xGoals' xGoals={player?.xGoals} isHorizontalList={isHorizontalList} isLineup={isLineup} /> :
                            player.shots && lineupTabActiveFilter === 'SHOTS' ? <ShirtIcon type='shots' shots={player?.shots} isHorizontalList={isHorizontalList} isLineup={isLineup} /> :
                                player.ballTouches && lineupTabActiveFilter === 'BALL_TOUCHES' ? <ShirtIcon type='ballTouches' ballTouches={player?.ballTouches} isHorizontalList={isHorizontalList} isLineup={isLineup} /> :
                                    <ShirtIcon type='empty' isLineup={isLineup} />
                    )}
                    {player?.secondYellowCard && player.secondYellowCard > 0 && !isInjuries ? (
                        <ShirtIcon type='secondYellowCard' isHorizontalList={isHorizontalList} isLineup={isLineup} />
                    ) : (
                        player?.yellowCard && !player.redCard && !isInjuries ? (
                            <ShirtIcon type='yellowCard' yellowCards={player?.yellowCard} isHorizontalList={isHorizontalList} isLineup={isLineup} />
                        ) : (
                            player.redCard ? (
                                <ShirtIcon type='redCard' isHorizontalList={isHorizontalList} isLineup={isLineup} />
                            ) : (
                                <ShirtIcon type='empty' isLineup={isLineup} />
                            )
                        )
                    )}   
                    {player?.assist && !isInjuries ? <ShirtIcon type='assist' assists={player?.assist} mb="mb-[0px]" isHorizontalList={isHorizontalList} isLineup={isLineup} /> : <ShirtIcon type='empty' mb="mb-[0px]" isLineup={isLineup} />}
                </div>
            </div>
        </div>
    )
}

export default ShirtV2;